import { VStack, Text, Stack, Button, Box } from '@chakra-ui/react';
import Joi from 'joi';
import { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useLogin } from 'utils/auth';
import { PageContext } from 'components/common/PageContext';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { caughtErrorGlobally } from 'utils/axios';
import { AxiosError } from 'axios';
import { Separator } from 'components/common/Separator';
import { FieldError } from 'react-hook-form';
import { PublicRoutes } from 'types/Routes';
import { InAppLayoutVariant } from 'types/Layout';
import { PasswordInputField } from 'components/common/Form/PasswordInputField';
import { GoogleButton } from 'components/atoms/GoogleButton';
import { AuthenticationType, EmailLoginCredentials } from '@novaheal/types';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { useFeatureFlagsContext } from 'hooks/utils/useFeatureFlagContext';
import { toastContent } from 'config/toast';
import { ErrorStatusType } from 'types/StatusTypes';
import { useSentry } from 'hooks/useSentry';
import { handleMutationError } from 'utils/handleMutationError';
import { AppleButton } from 'components/atoms/AppleButton';
import { Form, RHFFormInput } from 'components/common/Form/v2';
import { EmailIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { TermsPrivacyText } from 'components/atoms/TermsPrivacyText';

const schema = Joi.object({
  email: Joi.string()
    .email({
      tlds: {
        allow: false,
      },
    })
    .required()
    .messages({
      'string.empty': errorMessage.empty,
      'string.email': errorMessage.email,
    }),
  password: Joi.string().required().messages({
    'string.empty': errorMessage.empty,
  }),
});

export const Login = (): ReactElement => {
  const login = useLogin();
  const featureFlags = useFeatureFlagsContext();
  const toast = useNovaToast();
  const { setUser: setSentryUser } = useSentry();

  const handleLogin = async (values: EmailLoginCredentials): Promise<void> => {
    try {
      const { email } = values;
      const user = await login.mutateAsync({
        ...values,
        authenticationType: AuthenticationType.EMAIL,
        email: email.toLowerCase(),
      });
      setSentryUser(user?.id);
    } catch (error) {
      if (caughtErrorGlobally(error as AxiosError)) {
        handleMutationError(error);
        return;
      }
      toast.show(toastContent.error[ErrorStatusType.LOGIN]);
    }
  };

  return (
    <PageContext title="Login" headTitle="Login" variant={InAppLayoutVariant.TERTIARY}>
      <VStack w="full" px={{ base: 'l', md: 0 }}>
        <VStack gap="4xl" align="left" w="full">
          <VStack gap="2xl" align="left" w="full">
            <VStack align="left" gap="15px">
              <Text textStyle="h1.medium" color="text.primary">
                Account einloggen
              </Text>
              <Text textStyle="body.m.regular" color="text.secondary">
                Du hast noch keinen Account?{' '}
                <RouterLink to={PublicRoutes.REGISTER}>
                  <Text as="span" textStyle="body.m.medium" color="text.info">
                    Jetzt registrieren
                  </Text>
                </RouterLink>
              </Text>
            </VStack>
            <Form<EmailLoginCredentials, typeof schema> onSubmit={handleLogin} schema={schema}>
              {({ register, formState, watch, resetField }) => (
                <VStack gap="2xl" align="left" w="full">
                  <VStack gap="3" align="left" w="full">
                    <RHFFormInput
                      placeholder="Email"
                      name="email"
                      leftElement={<NovaIcon icon={EmailIcon} size="s" aria-hidden />}
                    />

                    <PasswordInputField
                      error={formState.errors.password as FieldError}
                      placeholder="Passwort"
                      register={() => register('password')}
                      value={watch('password')}
                      reset={() => resetField('password')}
                    />
                  </VStack>
                  <VStack align="self-end" w="full" mb="4px">
                    <RouterLink to={PublicRoutes.FORGOT_PASSWORD}>
                      <Text textStyle="body.s.regular" ml="4px" as="span" color="text.info">
                        Passwort vergessen?
                      </Text>
                    </RouterLink>
                  </VStack>

                  <Stack align="center" w="full">
                    <Button
                      variant="primary"
                      type="submit"
                      w="full"
                      disabled={!formState.isValid}
                      loading={login.isPending}
                      translate="no"
                    >
                      Einloggen
                    </Button>
                  </Stack>
                </VStack>
              )}
            </Form>
          </VStack>
          <VStack gap="2xl" align="left" w="full">
            <Box width="100%">
              <Separator text="Oder" />
            </Box>
            <VStack gap="m" align="left" w="full">
              {featureFlags.isEnabled('isGoogleAuthEnabled') && (
                <Box width="100%">
                  <GoogleButton />
                </Box>
              )}
              {featureFlags.isEnabled('isAppleAuthEnabled') && (
                <Box width="100%">
                  <AppleButton />
                </Box>
              )}
            </VStack>
            <TermsPrivacyText />
          </VStack>
        </VStack>
      </VStack>
    </PageContext>
  );
};
